import * as React from 'react';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';

type FieldOrientation = 'vertical' | 'horizontal';

interface FieldProps extends React.ComponentProps<'div'> {
  orientation?: FieldOrientation;
}

const Field = React.forwardRef<HTMLDivElement, FieldProps>(
  ({ className, orientation = 'vertical', ...props }, ref) => (
    <div
      ref={ref}
      data-slot="field"
      data-orientation={orientation}
      className={cn(
        'flex flex-col gap-2',
        orientation === 'horizontal' && 'flex-row items-start gap-2',
        className
      )}
      {...props}
    />
  )
);

Field.displayName = 'Field';

const FieldContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="field-content"
    className={cn('flex min-w-0 flex-1 flex-col gap-1', className)}
    {...props}
  />
));

FieldContent.displayName = 'FieldContent';

const FieldLabel = React.forwardRef<
  React.ElementRef<typeof Label>,
  React.ComponentPropsWithoutRef<typeof Label>
>(({ className, ...props }, ref) => (
  <Label
    ref={ref}
    data-slot="field-label"
    className={cn('m-0', className)}
    {...props}
  />
));

FieldLabel.displayName = 'FieldLabel';

const FieldDescription = React.forwardRef<
  HTMLParagraphElement,
  React.ComponentProps<'p'>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    data-slot="field-description"
    className={cn(
      'm-0 !mt-0 text-xs leading-[1.5] text-fg-secondary',
      className
    )}
    {...props}
  />
));

FieldDescription.displayName = 'FieldDescription';

const FieldError = React.forwardRef<
  HTMLParagraphElement,
  React.ComponentProps<'p'>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    data-slot="field-error"
    role="alert"
    className={cn(
      'm-0 !mt-0 text-xs leading-[1.5] text-status-danger',
      className
    )}
    {...props}
  />
));

FieldError.displayName = 'FieldError';

const FieldSet = React.forwardRef<
  HTMLFieldSetElement,
  React.ComponentProps<'fieldset'>
>(({ className, ...props }, ref) => (
  <fieldset
    ref={ref}
    data-slot="field-set"
    className={cn('m-0 min-w-0 border-0 p-0', className)}
    {...props}
  />
));

FieldSet.displayName = 'FieldSet';

const FieldLegend = React.forwardRef<
  HTMLLegendElement,
  React.ComponentProps<'legend'>
>(({ className, ...props }, ref) => (
  <legend
    ref={ref}
    data-slot="field-legend"
    className={cn(
      'mb-2 px-0 font-heading text-sm font-medium text-fg-primary',
      className
    )}
    {...props}
  />
));

FieldLegend.displayName = 'FieldLegend';

const FieldGroup = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="field-group"
    className={cn('flex flex-col gap-4', className)}
    {...props}
  />
));

FieldGroup.displayName = 'FieldGroup';

const FieldSeparator = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="field-separator"
    className={cn('h-px w-full bg-border-subtle', className)}
    {...props}
  />
));

FieldSeparator.displayName = 'FieldSeparator';

const FieldTitle = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="field-title"
    className={cn(
      'font-heading text-sm font-medium leading-[1.3] text-fg-primary',
      className
    )}
    {...props}
  />
));

FieldTitle.displayName = 'FieldTitle';

export {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSeparator,
  FieldSet,
  FieldTitle,
};
